import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of, forkJoin, Observable } from 'rxjs';
import { CommandService } from '@core/services/command.service';
import { SnackBarService } from '@core/services/snack-bar.service';
import { EntityChartApiService } from '../../services/entity-chart-service';
import {
  GET_ENTITIES_COUNT, GET_ENTITY_FILTER_GROUP_BY_REGION, GET_ENTITY_SETTINGS, GetEntityCountAction,
  GetEntityCountFailedAction, GetEntityCountSuccessAction, GetEntityFilterGroupByRegionAction,
  GetEntityFilterGroupByRegionFailAction, GetEntityFilterGroupByRegionSuccessAction, GetEntitySettingsAction,
  GetEntitySettingsFailedAction, GetEntitySettingsSuccessAction, POST_ENTITY_SETTINGS_ADD_ENTITY,
  POST_ENTITY_SETTINGS_DELETE_ENTITY, PostEntitySettingsAddEntity, PostEntitySettingsAddEntityFailed,
  PostEntitySettingsAddEntitySuccess, PostEntitySettingsDeleteEntity, PostEntitySettingsDeleteEntityFailed,
  PostEntitySettingsDeleteEntitySuccess, RESET_ENTITIES_FROM_PROJECT_SETTINGS, ResetEntitiesFromProjectSettingsAction,
  ResetEntitiesFromProjectSettingsSuccessAction, ResetEntitiesFromProjectSettingsFailedAction,
  GetEntityCountByCountrySuccessAction, GetEntityCountByCountryAction, GET_ENTITY_COUNT_BY_COUNTRY,
  GetEntityCountByCountryFailedAction
} from '../actions/entity-chart.actions';
import { EntitySetting } from '../../../models/entity-setting.model';

@Injectable()
export class EntityChartEffect {
  constructor (private actions$: Actions, private commandService: CommandService,
               private entityChartApi: EntityChartApiService,
               private snackBarService: SnackBarService,
               private fuseSplashScreenService: FuseSplashScreenService) {
  }


  getEntitySettings = createEffect(() => this.actions$.pipe(
    ofType(GET_ENTITY_SETTINGS),
    mergeMap((action: GetEntitySettingsAction) => {
      this.fuseSplashScreenService.show();
      return this.entityChartApi.getEntitySettings().pipe(
        map((payload: EntitySetting[]) => {
          this.fuseSplashScreenService.hide();
          return new GetEntitySettingsSuccessAction(payload);
        }),
        catchError(error => {
          this.fuseSplashScreenService.hide();
          this.snackBarService.showError('Countries fetch failed');
          return of(new GetEntitySettingsFailedAction(error));
        })
      );
    })
  ));

  getEntityCountByCountry = createEffect(() => this.actions$.pipe(
    ofType(GET_ENTITY_COUNT_BY_COUNTRY),
    mergeMap((action: GetEntityCountByCountryAction) => {
      this.fuseSplashScreenService.show();
      return this.entityChartApi.getEntityCountByCountry().pipe(
        map((payload: any[]) => {
          let entity={};
          if(payload && payload.length){
            payload.forEach(entityItem=>{
              entity[entityItem.countryCode.toUpperCase()]={
                proposedCount : entityItem.proposedCount,
                actualCount : entityItem.actualCount
              };
              //entity[entityItem.countryCode].actualCount = entityItem.actualCount;
            })
          }

          this.fuseSplashScreenService.hide();
          return new GetEntityCountByCountrySuccessAction(entity);
        }),
        catchError(error => {
          this.fuseSplashScreenService.hide();
          this.snackBarService.showError('Countries fetch failed');
          return of(new GetEntityCountByCountryFailedAction(error));
        })
      );
    })
  ));


  entityFilterFetchByGroup = createEffect(() => this.actions$.pipe(
    ofType(GET_ENTITY_FILTER_GROUP_BY_REGION),
    mergeMap((action: GetEntityFilterGroupByRegionAction) => {
      this.fuseSplashScreenService.show();
      return this.entityChartApi.getEntityGroupByRegion().pipe(
        map(payload => {
          this.fuseSplashScreenService.hide();
          return new GetEntityFilterGroupByRegionSuccessAction(payload);
        }),
        catchError(error => {
          this.fuseSplashScreenService.hide();
          return of(new GetEntityFilterGroupByRegionFailAction(error));
        })
      );
    })
  ));


  entityChartAddEntity$ = createEffect(() => this.actions$.pipe(
    ofType(POST_ENTITY_SETTINGS_ADD_ENTITY),
    mergeMap((action: PostEntitySettingsAddEntity) => {
        this.fuseSplashScreenService.show();
        return this.commandService.executeCommand(action.command).pipe(
          map(entityChartAddEntityNotification => {
            this.fuseSplashScreenService.hide();
            this.snackBarService.showInformation('Entity added');
            return new PostEntitySettingsAddEntitySuccess();
          }),
          catchError(error => {
            this.fuseSplashScreenService.hide();
            this.snackBarService.showError('Add country failed');
            return of(new PostEntitySettingsAddEntityFailed(error));
          })
        );
      }
    )
  ));


  entityChartDeleteEntity$ = createEffect(() => this.actions$.pipe(
    ofType(POST_ENTITY_SETTINGS_DELETE_ENTITY),
    mergeMap((action: PostEntitySettingsDeleteEntity) => {
        this.fuseSplashScreenService.show();
        return this.commandService.executeCommand(action.command).pipe(
          map(entityChartAddEntityNotification => {
            this.fuseSplashScreenService.hide();
            this.snackBarService.showInformation('Entity deleted');
            return new PostEntitySettingsDeleteEntitySuccess();
          }),
          catchError(error => {
            this.fuseSplashScreenService.hide();
            this.snackBarService.showError('Delete entity failed');
            return of(new PostEntitySettingsDeleteEntityFailed(error));
          })
        );
      }
    )
  ));


  getEntityCount = createEffect(() => this.actions$.pipe(
    ofType(GET_ENTITIES_COUNT),
    mergeMap((action: GetEntityCountAction) => {
        this.fuseSplashScreenService.show();
        return this.entityChartApi.getEntityCount().pipe(
          map((payload: number) => {
            this.fuseSplashScreenService.hide();
            return new GetEntityCountSuccessAction(payload);
          }),
          catchError(error => {
            this.fuseSplashScreenService.hide();
            return of(new GetEntityCountFailedAction(error));
          })
        );
      }
    )
  ));


  resetEntitiesFromProjectSettings= createEffect(() => this.actions$
    .pipe( ofType(RESET_ENTITIES_FROM_PROJECT_SETTINGS) )
    .pipe(
      mergeMap((action: ResetEntitiesFromProjectSettingsAction) => {
          this.fuseSplashScreenService.show();
          return this.commandService.executeCommand(action.command)
            .pipe(
              mergeMap((payload: any) => {
                this.snackBarService.showInformation('Entities Reset from Project Settings.');
                this.fuseSplashScreenService.hide();
                return [
                  new GetEntitySettingsAction(),
                  new ResetEntitiesFromProjectSettingsSuccessAction(payload)
                ];
              }),
              catchError((error) => {
                this.snackBarService.showError('Entites Reset from Project Settings Failed');
                this.fuseSplashScreenService.hide();
                return of(new ResetEntitiesFromProjectSettingsFailedAction(error));
              })
            );
        }
      )
    ));
}
